import React, { useContext, useEffect, useRef, useState} from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { BrandLogo, BrandSignate, MenuIcon, SearchIcon } from '$partials/icon';
import { HeaderNavigation } from './header-navigation.jsx';
import { ModalContext } from '$contexts/modal.jsx';
import { SearchModal } from '$partials/modals';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
  * @type {import('$types/components/partials/global.d.ts').Header}
*/
const Header = ({
    pageType,
    hasFeatureCarousel = false,
    hasHeroImage = false,
    headerLinks,
    logo = {},
    signate = {},
    ...props
}) => {
    let logoClassName = '';
    let containerClassName = '';
    let stickyNavClassName = '';
    let stickyNavWrapper = '';

    const navDialogRef = useRef();
    const stickyNavRef = useRef();
    const { handleModal, isModalOpen } = useContext(ModalContext);

    // state
    const [ navOpen, setNavOpen ] = useState(false);
    const [ scrollDirection, setScrollDirection ] = useState('up');
    const [ stickyNav, setStickyNav ] = useState(false);
    const [ navInitPos, setNavInitPos ] = useState(0);

    // methods
    const handleCloseClick = () => {
        setNavOpen(false);
    };

    const handleNavClick = () => {
        setNavOpen(!navOpen);
    };

    useEffect(() => {
        setNavInitPos(window?.pageYOffset + stickyNavRef.current?.getBoundingClientRect()?.top);
    }, [stickyNavRef.current]);

    useEffect(() => {
        const threshold = 4;

        let prevY = window.pageYOffset;
        let tick = false;

        const updateScrollDirection = () => {
            const currentY = window.pageYOffset;

            if (Math.abs(currentY - prevY) < threshold) {
                tick = false;
                return;
            }

            setScrollDirection(currentY > prevY ? 'down' : 'up');


            if((currentY > navInitPos) && navInitPos > 0) {
                setStickyNav(true);
            } else {
                setStickyNav(false);
            }

            prevY = (currentY > 0) ?  currentY : 0;
            tick = false;
        };

        const handleScroll = () => {
            if (!tick) {
                window.requestAnimationFrame(updateScrollDirection);
                tick = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrollDirection]);

    useEffect(() => {
        if (navDialogRef.current) {
            if (navOpen) {
                disableBodyScroll(navDialogRef.current);
            } else {
                enableBodyScroll(navDialogRef.current);
            }
        }
    }, [ navOpen ]);

    switch (pageType) {
        default:
            logoClassName = `${hasHeroImage ? ` z1 mt3 sm-mt5 md-mt7${stickyNav ? ' mt8 sm-mt9 md-mt0' : ''}` : ' block sm-mb0 sm-flex mr0 sm-mr9'}`;
            containerClassName = `${hasHeroImage ? ' flex-col-reverse mt0 md-mt8' : ` flex-wrap sm-flex-no-wrap mt5 sm-mt6 md-mt9 mb7 sm-mb10 md-mb11${stickyNav ? ' pb8 sm-pb0' : ''}`}`;
            stickyNavWrapper = `${ hasHeroImage ? ' mt3 sm-mt5 md-mt3 top-0 md-absolute md-trunk-y-bleed md-min-height-100vh' : ' sticky-nav__wrapper md-mt0'}`;
            stickyNavClassName = `${hasHeroImage ? `${!stickyNav ? ' pt5 md-pb6' : ''} top-0 md-container` : `${!stickyNav ? ' pt6 sm-pt6 md-pb0' : ''}`} items-center`;
            break;
        case 'Home':
            logoClassName = ` block md-block sm-mb0 sm-mr9 md-mr0${hasFeatureCarousel ? ' mt9 sm-mt7 md-mtn-7-2 md-mb8' : ' mt9 md-mb8' }`;
            containerClassName = ` flex-wrap sm-flex-no-wrap md-flex-wrap${stickyNav ? ` pb8 sm-pb0${!hasFeatureCarousel ? ' md-pb7' : ''}` : ''}`;
            stickyNavWrapper = ` md-mt0 sm-mt7 sticky-nav__wrapper${hasFeatureCarousel ? ' md-absolute md-trunk-y-bleed md-min-height-100vh' : ''}`;
            stickyNavClassName = `${hasFeatureCarousel ? ` ${!stickyNav ? 'pt6 sm-pt6 md-pt5 md-pb5 md-mt3' : ''} items-center md-container md-mb0` : ` ${!stickyNav ? 'pt5' : ''} sm-items-end items-center`}`;
            break;
    }

    return (
        <header className={`container flex${containerClassName}`}>
            <div className={logoClassName}>
                <a href='/'>
                    {pageType === 'home' && (
                        <h1 className='accessible-hide'>
                            Homepage
                        </h1>
                    )}

                    {hasHeroImage ? (
                        signate?.url ? (
                            <img
                                className='signate'
                                src={signate.url} />
                        ) : (
                            <BrandSignate />
                        )
                    ) : (
                        logo?.url ? (
                            <img
                                className='logo'
                                src={logo.url} />
                        ) : (
                            <BrandLogo />
                        )
                    )}
                </a>
            </div>
            <div
                className={`pointer-events-none flex width-100${stickyNavWrapper}`}>
                <div className={`sm-flex md-block width-100 ${stickyNav && !isModalOpen && !navOpen ? ' fixed trunk-y-bleed top-0 bg-grey z4' : ''}`}>
                    <div
                        className={`pointer-events-auto flex justify-between width-100 md-items-end ${stickyNav && !isModalOpen && !navOpen ? 'py5 container' : 'sm-pb0' }${stickyNavClassName}`}
                        ref={stickyNavRef}>
                        <div className='flex'>
                            <button
                                className='form-field-reset link-reset cursor-pointer inline-flex items-center mr8'
                                onClick={handleNavClick}
                                type='button'>
                                <MenuIcon className='mr5'/>
                                <p className='type-style-5'>Menu</p>
                            </button>
                            {headerLinks && headerLinks.map((item) => {
                                const linkTo = item.link?.linkTo;
                                let link = '';
                                switch (linkTo) {
                                    case 'email':
                                        link = 'mailto:' + item.link[linkTo];
                                        break;
                                    case 'anchor':
                                        link = item.link[linkTo];
                                        break;
                                    case 'custom_url':
                                        link = item.link?.customUrl;
                                        break;
                                    case 'file':
                                        break;
                                    case 'page':
                                        link = item.link[linkTo]?.url;
                                        break;
                                    case 'phone':
                                        link = 'tel:' + item.link[linkTo];
                                        break;
                                }

                                return (
                                    <a
                                        className='hide sm-show mr8 link-reset type-style-5'
                                        href={link}
                                        key={item?.id}
                                        rel='noreferrer'
                                        target={item.link?.new_window || item.link?.linkTo === 'file' ? '_blank' : ''}>
                                        {item.label}
                                    </a>
                                );
                            })}
                        </div>
                        <div className='flex items-center'>
                            <button
                                className='form-field-reset link-reset inline-flex items-center type-style-5 cursor-pointer'
                                onClick={() => handleModal(<SearchModal {...props}/>)}
                                type='button'>
                                <span className='hide md-show'>Find Properties</span>
                                <SearchIcon className='md-ml5'/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <HeaderNavigation
                closeClick={handleCloseClick}
                open={navOpen}
                ref={navDialogRef}
                signate={signate}
                {...props}/>
        </header>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Header;
